import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  Col,
  Container,
  CustomInput,
  Form, FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Row,
  Spinner
} from 'reactstrap';
import { validateURL } from '../../utils/inputs/url';

import DeleteAccount from 'components/utils/user/DeleteAccount';
import ChangePassword from 'components/utils/user/ChangePassword';
import { AuthContext } from '../../../providers/AuthProvider';
import { deleteAccount, dispatchError, updateAttributes, changePassword, getCurrentUserProfileById, overlayToggle } from 'actions/user/profile';
import { Alerts, ErrorMessage, SuccessMessage } from '../../utils/alerts';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {
  countries as selectableCountries,
} from 'components/metadata/SelectOptions';
import Tags from 'components/metadata/Tags';

import MailChimp from '../../utils/MailChimp';
import { ProfileState } from '../../../reducers/user/profile';
import { CropperModal } from './CropperModal';
import { API } from 'aws-amplify';
import { Profile as ProfileType, profileType } from 'types/Profile';

import './Profile.scss';

import { OptionType } from '../../../types/SelectTypes';
import { viewProfileURL } from 'urls';

interface Props extends RouteComponentProps, ProfileState {
  deleteAccount: Function;
  dispatchError: Function;
  updateAttributes: Function;
  changePassword: Function;
  getCurrentUserProfile: Function;
  overlayToggle: Function;
}

interface State extends Partial<ProfileType>, Alerts {
  email: string;
  websiteValid?: boolean;
  cropperModalOpen: boolean;
  changePasswordVisible: boolean;
}

class Profile extends React.Component<Props, State> {
  static contextType = AuthContext;
  private _isMounted;

  constructor(props: Props) {
    super(props);
    this._isMounted = false;

    this.state = {
      email: '',
      cropperModalOpen: false,
      changePasswordVisible: false
    };
  }

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    await this.getUserCredentials();
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  async componentDidUpdate(prevProps: Readonly<Props>): Promise<void> {
    const context: React.ContextType<typeof AuthContext> = this.context;
    if (this.props.accountDeleted) {
      try {
        await context.logout();
        this.props.history.push('/');
      } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
        this.props.dispatchError();
      }
    }

    if (!prevProps.details && this.props.details && this._isMounted) {
      this.setState({ ...this.props.details });
    }
  }

  getUserCredentials = async (): Promise<void> => {
    const context: React.ContextType<typeof AuthContext> = this.context;
    if (!this.props.details) {
      await this.props.getCurrentUserProfile(context.uuid);
    }

    if (this._isMounted) {
      this.setState({ email: context.email, ...this.props.details });
    }
  };

  submitForm = async (): Promise<void> => {
    try {
      this.props.overlayToggle(true);
      const context: React.ContextType<typeof AuthContext> = this.context;
      const attributes = {};
      const profileAttributes = {};

      if (this._isMounted) {
        if (this.state.email !== context.email) {
          Object.assign(attributes, {'email': this.state.email});
        }

        if (Object.keys(attributes).length) {
          await this.props.updateAttributes(attributes);
        }

        [
          'social_media',
          'contributors',
          'profile_image',
          'featured_image',
          'full_name',
          'field_expertise',
          'city',
          'country',
          'biography',
          'website',
          'public_profile',
          'affiliation',
          'position',
          'contact_person',
          'contact_position',
          'contact_email',
          'concept_tags',
          'profile_type',
          'community_link'
        ].forEach( attr => {
          if (typeof this.state[attr] === 'undefined' || this.state[attr] === null) {
            return;
          } else {
            const value = attr === 'concept_tags'
              ? (this.state[attr] || []).map((id) => Number(id)) // concept tags must be number arr
              : this.state[attr]

            Object.assign(profileAttributes, { [attr]: value })
          }
        });

        if (Object.keys(profileAttributes).length) {
          await API.patch('tba21', 'profiles', {
            body: profileAttributes
          });
        }

        await this.getUserCredentials();
      }
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (this._isMounted) {
        this.setState({ errorMessage: 'We had an issue updating your profile.' });
      }
    } finally {
      this.props.overlayToggle(false);
    }
  };

  fieldChanged = (value: string | string[] | boolean, field: string) => {
    const state = {};
    if (
      ((typeof value === 'string' && value.length) || Array.isArray(value) || typeof value === 'boolean'
      ) && this._isMounted) {
      Object.assign(state, { [field]: value });
    }

    this.setState(state);
  };

  onChangeSocialMedia = (newValue: any, actionMeta: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    this.fieldChanged(
      (newValue && newValue.length > 0) ? newValue.filter(n => {
        let value = n.value;
        if (!(value.startsWith('http://') || value.startsWith('https://'))) {
          value = 'https://' + value;
        }
        if (!validateURL(value)) {
          alert('please enter a valid web address');
          return false;
        } else {
          return true;
        }
      }).map(e => {
        let value = e.value;
        if (!(value.startsWith('http://') || value.startsWith('https://'))) {
          value = 'https://' + value;
        }
        return value;
      }) : [],
      'social_media'
    );
  };

  onTagsChange = (tags) => {
    console.log(tags);
    
    const tagList = (tags || []).map((tag) => tag.id)
    this.fieldChanged(tagList, 'concept_tags')
  }

  render() {
    const {
      full_name,
      city,
      field_expertise,
      country,
      website,
      affiliation,
      position,
      biography,
      social_media,
      profile_type,
      profile_image,
      public_profile,
      contact_email,
      contact_person,
      contact_position,
      aggregated_concept_tags,
      community_link
    } = this.state;

    const context: React.ContextType<typeof AuthContext> = this.context;
    const countryList = country ? selectableCountries.find(a => a.value === country) : undefined;

    const conceptTags = aggregated_concept_tags?.map((t) => ({
      id: t.id,
      value: t.id,
      label: t.tag_name,
    }))

    const profileTypeOptions = Object.entries(profileType).map(([value, label]) => ({ value, label }))

    return (
      <Container className="profile-editor">

        <ErrorMessage message={this.props.errorMessage} />
        <SuccessMessage message={this.props.successMessage} />

        <CropperModal
          imageURL={profile_image ? profile_image : undefined}
          open={this.state.cropperModalOpen}
          callback={ async (openState, updated) => {
            if (this._isMounted) {
              if (updated) {
                await this.props.getCurrentUserProfile(context.uuid);
              }
              this.setState({ cropperModalOpen: openState });
            }
          }}
        />

        { this.props.overlay ?
          <div className="overlay_fixed_middle">
            <div className="middle">
              <Spinner type="grow"/>
            </div>
          </div>
          : <></>
        }
        <Form
          id="user-details"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault()
            this.submitForm()
          }}
        >
          <Row>
            <Col xs="12" md="4">
              <div className="profile-editor__left">
                <h1>{!!full_name ? `Hey, ${full_name}` : 'Your Profile'}</h1>

                <div className="profile-editor__image" onClick={() => {
                  this._isMounted && this.setState({ cropperModalOpen: true })
                }}>
                  { profile_image
                    ? <img src={profile_image} alt="" />
                    : <div>Click here to upload a profile image</div>
                  }
                </div>

                <FormGroup className="pt-4">
                  <InputGroup>
                    <CustomInput
                      type="switch"
                      id="public"
                      name="public"
                      label="Make my profile public."
                      checked={!!public_profile ? public_profile : false}
                      onChange={e => this.fieldChanged(e.target.checked, 'public_profile')}
                    />
                    <small>Untick this option if you do not wish your profile to be viewable by the general public. If you are a contributor, your uploaded content will still be visible to everyone.</small>
                  </InputGroup>
                </FormGroup>

                <div className="profile-editor__actions">
                  { this.state.changePasswordVisible
                    ? <ChangePassword changePassword={this.props.changePassword} />
                    : <>
                        <button className="button button--small" onClick={() => this.setState({ changePasswordVisible: true })}>Change password</button>
                        <DeleteAccount deleteAccountAction={this.props.deleteAccount}/>
                      </>
                  }
                </div>
                {/* <Row style={{paddingBottom: '1rem'}}>
                  <a href={viewProfileURL(this.props.details?.id || '')} target="_blank" rel="noopener noreferrer">View my current profile</a>
                </Row> */}
              </div>
            </Col>
            <Col xs="12" md="8">
              <div className="profile-editor__right">
                <FormGroup>
                  <Label for="email">Email Address</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email"
                    onChange={e => this.fieldChanged(e.target.value, 'email')}
                    defaultValue={context.email}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="full_name">Full Name</Label>
                  <Input type="text" name="full_name" id="full_name" placeholder="Full Name" onChange={e => this.fieldChanged(e.target.value, 'full_name')} defaultValue={full_name ? full_name : ''} />
                </FormGroup>
                <FormGroup>
                  <Label for="field_expertise">Field of Expertise</Label>
                  <Input type="text" name="field_expertise" id="field_expertise" placeholder="Field of Expertise" onChange={e => this.fieldChanged(e.target.value, 'field_expertise')} defaultValue={field_expertise ? field_expertise : ''} />
                </FormGroup>
                <FormGroup>
                  <Label for="profile_type">Profile Type</Label>
                  <Select
                    className="select"
                    classNamePrefix="select"
                    menuPlacement="auto"
                    placeholder="Profile Type"
                    options={ profileTypeOptions }
                    value={ profileTypeOptions.find(a => a.value === profile_type) }
                    onChange={ e => this.fieldChanged((e as OptionType).value, 'profile_type') }
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="city">City</Label>
                  <Input type="text" name="city" id="city" placeholder="City" onChange={e => this.fieldChanged(e.target.value, 'city')} defaultValue={city ? city : ''} />
                </FormGroup>
                <FormGroup>
                  <Label for="country">Country</Label>
                  <Select
                    className="select"
                    classNamePrefix="select"
                    isSearchable
                    menuPlacement="auto"
                    placeholder="Country"
                    options={selectableCountries}
                    value={countryList ? countryList : null}
                    onChange={e => this.fieldChanged((e as OptionType).value, 'country')}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="biography">Biography (max 1024 characters)</Label>
                  <Input
                    type="textarea"
                    id="biography"
                    value={biography ? biography : ''}
                    onChange={e => this.fieldChanged(e.target.value, 'biography')}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="website">Website</Label>
                  <Input
                    type="text"
                    name="website"
                    id="website"
                    placeholder="Website"
                    invalid={!!this.state.websiteValid && !this.state.websiteValid}
                    defaultValue={website ? website : ''}
                    onChange={e => {
                      let value = e.target.value;
                      if (!(value.startsWith('http://') || value.startsWith('https://'))) {
                        value = 'https://' + value;
                      }
                      let valid = validateURL(value);
                      if (!value || (value && !value.length)) valid = true // set valid to true for no content
                      if (valid) this.fieldChanged(value, 'website') // if valid set the data in changedItem
                      if (this._isMounted) this.setState({ websiteValid: valid })
                    }}
                  />
                  <FormFeedback>Not a valid URL</FormFeedback>
                </FormGroup>
                <FormGroup>
                  <Label for="community-link">ocean comm/uni/ty Profile</Label>
                  <Input
                    type="text"
                    name="community-link"
                    id="community-link"
                    placeholder="ocean comm/uni/ty Profile"
                    defaultValue={ community_link || '' }
                    onChange={e => {
                      let value = e.target.value
                      if (!(value.startsWith('http://') || value.startsWith('https://'))) {
                        value = 'https://' + value
                      }
                      this.fieldChanged(value, 'community_link')
                    }}
                  />
                  <FormFeedback>Not a valid URL</FormFeedback>
                </FormGroup>
                <FormGroup>
                  <Label for="social_media">Social Media URL(s)</Label>
                  <CreatableSelect
                    className="select"
                    classNamePrefix="select"
                    isMulti
                    menuPlacement="auto"
                    placeholder="Social Media"
                    onChange={this.onChangeSocialMedia}
                    value={
                      !!social_media && social_media.length > 0 ?
                        social_media.map(s => ({ value: s, label: s })) : []
                    }
                    formatCreateLabel={i => `Add new social media profile URL ${i}`}
                  />
                </FormGroup>
                { !!conceptTags && 
                  <FormGroup>
                      <Label for="concept_tags">Concept Tag(s)</Label>
                      <Tags
                        className="concept_tags"
                        type="concept"
                        defaultValues={conceptTags}
                        callback={ (tags) => this.onTagsChange(tags) }
                      />
                  </FormGroup>
                }
                <FormGroup>
                  <Label for="affiliation">Affiliation</Label>
                  <Input type="text" name="affiliation" id="affiliation" placeholder="Affiliation" onChange={e => this.fieldChanged(e.target.value, 'affiliation')} defaultValue={affiliation ? affiliation : ''} />
                </FormGroup>
                <FormGroup>
                  <Label for="position">Position</Label>
                  <Input type="text" name="position" id="position" placeholder="Position" onChange={e => this.fieldChanged(e.target.value, 'position')} defaultValue={position ? position : ''} />
                </FormGroup>
                {
                  ((!!profile_type && profile_type === 'Institution')) ||
                  ((!!profile_type && profile_type === 'Collective')) ?
                    <>
                      <FormGroup>
                        <Label for="contact_person">Contact Person</Label>
                        <Input type="text" name="contact_person" id="contact_person" placeholder="Contact Person" onChange={e => this.fieldChanged(e.target.value, 'contact_person')} defaultValue={contact_person ? contact_person : ''} />
                      </FormGroup>
                      <FormGroup>
                        <Label for="contact_position">Contact Position</Label>
                        <Input type="text" name="contact_position" id="contact_position" placeholder="Contact Position" onChange={e => this.fieldChanged(e.target.value, 'contact_position')} defaultValue={contact_position ? contact_position : ''} />
                      </FormGroup>
                      <FormGroup>
                        <Label for="contact_email">Contact Email</Label>
                        <Input type="text" name="contact_email" id="contact_email" placeholder="Contact Email" onChange={e => this.fieldChanged(e.target.value, 'contact_email')} defaultValue={contact_email ? contact_email : ''} />
                      </FormGroup>
                    </>
                    :
                    <></>
                }

                <div className="profile-editor__save">
                  <button className="button" form="user-details">Save</button>
                </div>
              </div>

            </Col>
          </Row>
        </Form>

        {/* { context.email ?
          <>
            <h3>Communication Preferences</h3>
            <MailChimp email={context.email}/>
          </>
          : <></>
        } */}

      </Container>
    )
  }
}

const mapStateToProps = (state: { profile: Props }) => ({
  errorMessage: state.profile.errorMessage,
  successMessage: state.profile.successMessage,

  updateAttributes: state.profile.updateAttributes,
  accountDeleted: state.profile.accountDeleted,
  overlay: state.profile.overlay,

  details: state.profile.details
});

const mapDispatchToProps = {
  deleteAccount,
  dispatchError,
  updateAttributes,
  changePassword,
  getCurrentUserProfile: getCurrentUserProfileById,
  overlayToggle
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Profile));
